import {Badge, Text} from '@mantine/core'
import {IconAlertCircle, IconCheck} from '@tabler/icons-react'
import type {CSSProperties} from 'react'

import {formatPrice} from '../lib/format-price'
import {ageInDays, type BillingSignal, deriveBillingSignal} from './project-billing-signal'
import classes from './project-list-card.module.css'
import type {ProjectListItem} from './schemas'

type ProjectBillingRailProps = {
    project: ProjectListItem
}

type VerdictTokens = {
    label: string
    badgeBg: string
    badgeColor: string
    railBg: string
    mobileBorder: string
    icon?: 'alert' | 'check'
    amountColor: string
}

/** Colour tokens */
const VERDICT_TOKENS: Record<Exclude<BillingSignal['type'], 'none'>, VerdictTokens> = {
    'ready-to-bill': {
        label: 'Ready to bill',
        badgeBg: '#ffe3e3',
        badgeColor: '#c92a2a',
        railBg: '#fffafa',
        mobileBorder: '#ffe3e3',
        icon: 'alert',
        amountColor: '#1a1b1e',
    },
    accruing: {
        label: 'Accruing',
        badgeBg: '#fff4e6',
        badgeColor: '#e8590c',
        railBg: '#fffdf8',
        mobileBorder: '#ffe8cc',
        amountColor: '#495057',
    },
    'awaiting-quickbooks': {
        label: 'Awaiting Quickbooks',
        badgeBg: '#fff9db',
        badgeColor: '#e67700',
        railBg: '#fffef7',
        mobileBorder: '#fff3bf',
        icon: 'alert',
        amountColor: '#495057',
    },
    'fully-billed': {
        label: 'Fully billed',
        badgeBg: '#ebfbee',
        badgeColor: '#2b8a3e',
        railBg: '#ffffff',
        mobileBorder: '#e9ecef',
        icon: 'check',
        amountColor: '#ced4da',
    },
}

function verdictIcon(icon: VerdictTokens['icon']) {
    if (icon === 'alert') {
        return <IconAlertCircle size={11} stroke={2.6} />
    }
    if (icon === 'check') {
        return <IconCheck size={11} stroke={3} />
    }
    return undefined
}

export function ProjectBillingRail({project}: ProjectBillingRailProps) {
    const signal = deriveBillingSignal({
        unbilledCount: project.unbilledCount,
        unbilledTotal: project.unbilledTotal,
        unbilledOnDraftTotal: project.unbilledOnDraftTotal,
        billedOutCount: project.billedOutCount,
        plannedCount: project.plannedCount,
        activeCount: project.activeCount,
    })

    // A project with no charges still gets a rail, so the money column is
    // unbroken down the stack and an empty rail never reads as a missing one.
    if (signal.type === 'none') {
        return (
            <div className={classes['rail']} data-verdict="none">
                <Text className={classes['railEmpty']}>Nothing billable yet</Text>
            </div>
        )
    }

    const tokens = VERDICT_TOKENS[signal.type]
    const railStyle = {
        '--rail-bg': tokens.railBg,
        '--rail-mobile-border': tokens.mobileBorder,
    } as CSSProperties

    const age = project.oldestUnbilledAt != null ? ageInDays(project.oldestUnbilledAt) : null

    // A fully-billed project has no unbilled money, so the pill is the whole rail: a
    // $0 in the money column is a figure to read and dismiss on every single scan.
    const isFullyBilled = signal.type === 'fully-billed'

    const caption = isFullyBilled ? null : (
        <>
            {project.unbilledCount} charge{project.unbilledCount === 1 ? '' : 's'}
            {age != null && (
                <>
                    {' '}
                    &middot; oldest{' '}
                    <Text span fw={700} c={tokens.badgeColor} inherit>
                        {age} day{age === 1 ? '' : 's'}
                    </Text>
                </>
            )}
        </>
    )

    return (
        <div className={classes['rail']} data-verdict={signal.type} style={railStyle}>
            <div className={classes['railContent']}>
                <Badge
                    variant="light"
                    leftSection={verdictIcon(tokens.icon)}
                    className={`${classes['pill']} ${classes['railBadge']}`}
                    styles={{root: {backgroundColor: tokens.badgeBg, color: tokens.badgeColor}}}
                >
                    {tokens.label}
                </Badge>
                {!isFullyBilled && (
                    <Text className={classes['amount']} style={{color: tokens.amountColor}}>
                        {formatPrice(project.unbilledTotal, {showCents: false})}
                    </Text>
                )}
                {caption != null && <Text className={classes['railCaption']}>{caption}</Text>}
            </div>
        </div>
    )
}
